/**
 * droid_templates.ts — PREBUILT DROID PROGRAMS (2026-09-24): ready-made DroidScript 2 programs the
 * terminal offers per class. Each is a plain program — load it, change the words, upload it.
 * Every template must compile for every class it lists (verify:droid-lang pins it).
 */
import type { DroidClass } from './droid_classes';

export interface DroidTemplate {
  key: string;
  name: string;
  blurb: string;
  classes: DroidClass[];
  source: string;
}

const ALL: DroidClass[] = ['battle', 'supply', 'medic', 'humanoid', 'ship', 'mega'];

export const DROID_TEMPLATES: DroidTemplate[] = [
  {
    key: 'hello', name: 'Hello, world', classes: ALL,
    blurb: 'The smallest program: say something, look around, stop.',
    source: `# Hello, world — the smallest program.
say "Hello! I am online."
wait 5
scan
`,
  },
  {
    key: 'organize', name: 'Storage organizer', classes: ['supply', 'humanoid'],
    blurb: 'Tidies your structure storage every half hour: merges stacks, racks the weapons, cools the food.',
    source: `# Storage organizer — tidies your structure storage every half hour.
# Deploy the droid near your house or hall.
forever
  let s = structure(300)
  if s
    goto s
    organize
    say "Storage tidied — " + result()
  else
    say "I need a structure of yours within 300 m"
  end
  home
  wait 1800
end
`,
  },
  {
    key: 'extractor', name: 'Extractor run', classes: ['supply', 'humanoid'],
    blurb: 'Empties your extractor\'s hopper and carries the haul into your storage, every 15 minutes.',
    source: `# Extractor run — collects from your extractor and stores the haul.
forever
  let e = extractor(800)
  if not e
    say "No extractor of yours within 800 m"
    wait 300
    continue
  end
  goto e
  collect
  if ok()
    let s = structure(800)
    if s
      goto s
      store all
      say "Stored the haul: " + result()
    end
  end
  home
  wait 900
end
`,
  },
  {
    key: 'patrol', name: 'Perimeter patrol', classes: ALL,
    blurb: 'Walks a square around the home point and scans for wildlife at every lap.',
    source: `# Perimeter patrol — a square around the home point, a wildlife scan each lap.
let r = 40
forever
  route home_x() + r, home_z() - r, home_x() + r, home_z() + r, home_x() - r, home_z() + r, home_x() - r, home_z() - r
  scan life
  wait 10
end
`,
  },
  {
    key: 'survey', name: 'Resource survey', classes: ['battle', 'supply', 'humanoid', 'mega'],
    blurb: 'Walks a grid across the terrain, samples a resource family and files a report with the best spots.',
    source: `# Resource survey — samples a family across the terrain and files a REPORT.
# Families: ferrite_ore, noble_ore, crystal, stone, reagent, gas, solvent,
#           fiber, timber, resin, hide, grain, produce …
let family = "ferrite_ore"
survey (family) 250 50
say "Survey filed: " + result()
home
`,
  },
  {
    key: 'greeter', name: 'Greeter & crier', classes: ALL,
    blurb: 'Strolls near home, greets travelers and townsfolk by name and calls out an advert every minute.',
    source: `# Greeter & crier — greets passers-by and calls out an advert.
every 60
  advertise "Welcome, traveler! Fine goods at my owner's stall."
end
on player_near 8
  greet player
end
on versona_near 6
  greet versona
end
wander 12 3600
loop
`,
  },
  {
    key: 'vendor', name: 'Vendor stall', classes: ['supply', 'humanoid'],
    blurb: 'Sells the goods you loaded into its cargo to travelers (K near it to browse). Set your prices on the first lines.',
    source: `# Vendor stall — load goods at the terminal (Cargo), set prices here.
price ferrite_ore 3
price crystal 8
say "The stall is open!"
every 90
  advertise "Ore and crystal at fair prices — press K by me to browse!"
end
on sale
  say "Thank you! Come again."
end
forever
  if cargo() == 0
    say "Sold out — thank you all!"
    stop
  end
  wait 30
end
`,
  },
  {
    key: 'market', name: 'Market poster', classes: ['supply', 'humanoid'],
    blurb: 'Walks to the nearest town and posts cargo on the bazaar, sells the rest to the town stores.',
    source: `# Market poster — lists cargo on the bazaar in the nearest town.
let t = town()
goto t
post ferrite_ore 300 100
say "Posted: " + result()
sell fiber
home
`,
  },
  {
    key: 'guard', name: 'Guard post', classes: ['battle', 'humanoid', 'mega'],
    blurb: 'Holds the home point and fights any creature that comes close; falls back when badly hurt.',
    source: `# Guard post — first matching handler wins, so retreat is checked before fighting.
on hurt 30
  say "Falling back!"
  home
end
on creature_near 30
  attack nearest
end
home
`,
  },
  {
    key: 'medic', name: 'Field medic', classes: ['medic', 'humanoid'],
    blurb: 'Follows you and heals anyone injured nearby — you, travelers, townsfolk, droids.',
    source: `# Field medic — follows you and heals the injured.
on injured 20
  let p = injured(20)
  goto p
  heal any
end
forever
  follow 30
end
`,
  },
  {
    key: 'ship', name: 'Ship technician', classes: ['ship'],
    blurb: 'Reports your active ship, fits ship parts from the cargo, loads cargo into the hold at a spaceport.',
    source: `# Ship technician — status report, fittings, and the hold.
ship_status
ship_fit
if cargo() > 0
  ship_load all
end
note "Hold after loading: " + str(ship().hold_used)
report "Ship check"
`,
  },
  {
    key: 'hauler', name: 'Supply hauler', classes: ['supply', 'humanoid', 'ship'],
    blurb: 'Takes goods out of your storage and brings them to you.',
    source: `# Supply hauler — fetches from your storage and hands it to you.
let what = "ferrite_ore"
let s = structure(600)
goto s
take (what) 100
come
give all
say "Delivered " + what
home
`,
  },
  {
    key: 'caretaker', name: 'Caretaker', classes: ['humanoid', 'medic', 'ship'],
    blurb: 'Keeps your structure repaired and its maintenance pool topped up from your wallet.',
    source: `# Caretaker — repairs and tops up your structure every 20 minutes.
forever
  let s = structure(300)
  if s
    goto s
    if s.condition < 900
      repair
    end
    if s.maint < 200
      upkeep 100
    end
  end
  wait 1200
end
`,
  },
  {
    key: 'crafter', name: 'Component crafter', classes: ['humanoid'],
    blurb: 'Turns ore in the cargo into metal ingots near a town or your structure.',
    source: `# Component crafter — ingots from ferrite ore (20 ore each).
forever
  if cargo(ferrite_ore) >= 20
    craft metal_ingot
    say result()
  else
    say "Out of ore — load more at the terminal"
    wait 600
  end
  wait 5
end
`,
  },
  {
    key: 'defender', name: 'Mega defender', classes: ['mega'],
    blurb: 'A tank-sized guard that sweeps a wide circle and engages the strongest threat first.',
    source: `# Mega defender — hold ground, then sweep.
on creature_near 60
  attack strongest
end
forever
  guard 60 300
  patrol 80
end
`,
  },
];

export function templatesFor(cls: DroidClass): DroidTemplate[] {
  return DROID_TEMPLATES.filter((t) => t.classes.includes(cls));
}
